import {
  TREATMENT_CATEGORIES,
  TREATMENT_SCHEMAS,
  TREATMENT_TYPE_OPTIONS,
  getTreatmentOptionsByCategory,
} from "./treatmentSchemas";

export function createTreatmentId() {
  return `tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function getDefaultTreatmentCategory() {
  return TREATMENT_CATEGORIES[0] || "Procedure";
}

export function getDefaultTreatmentType(category = getDefaultTreatmentCategory()) {
  const options = getTreatmentOptionsByCategory(category);
  return options[0] || TREATMENT_TYPE_OPTIONS[0];
}

export function createBlankTreatment(type = getDefaultTreatmentType()) {
  const schema =
    TREATMENT_SCHEMAS[type] ||
    TREATMENT_SCHEMAS[getDefaultTreatmentType()];

  const details = {};

  (schema.detailFields || []).forEach((field) => {
    details[field.name] = field.type === "multiSelect" ? [] : "";
  });

  return {
    id: createTreatmentId(),
    category: schema.category || getDefaultTreatmentCategory(),
    type: schema.type,
    timestamp: "",
    performedBy: "",
    successful: "",
    notes: "",
    details,
  };
}

export function normalizeTreatmentForEdit(treatment = {}) {
  const base = createBlankTreatment(
    treatment.type || getDefaultTreatmentType(treatment.category)
  );

  const incomingDetails = treatment.details || {};

  return {
    ...base,
    ...treatment,
    category:
      treatment.category ||
      TREATMENT_SCHEMAS[treatment.type]?.category ||
      base.category,
    details: {
      ...base.details,
      ...incomingDetails,
    },
  };
}

export function getTreatmentSchema(type = "") {
  return (
    TREATMENT_SCHEMAS[type] ||
    TREATMENT_SCHEMAS[getDefaultTreatmentType()]
  );
}

export function buildTreatmentSummaryParts(treatment = {}) {
  const schema = getTreatmentSchema(treatment.type);
  const details = treatment.details || {};
  const parts = [];

  (schema.summaryFields || []).forEach((fieldName) => {
    const field = (schema.detailFields || []).find(
      (item) => item.name === fieldName
    );

    const value = details[fieldName];

    if (
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return;
    }

    const displayValue = Array.isArray(value) ? value.join(", ") : String(value);

    if (field?.suffix) {
      const suffix = field.suffix;
      const lower = displayValue.toLowerCase();
      const suffixLower = suffix.toLowerCase();

      parts.push(lower.includes(suffixLower) ? displayValue : `${displayValue} ${suffix}`);
      return;
    }

    if (fieldName === "fluidVolume") {
      parts.push(displayValue.toLowerCase().includes("ml") ? displayValue : `${displayValue} mL`);
      return;
    }

    if (fieldName === "flowRate" || fieldName === "o2FlowRate") {
      parts.push(displayValue.toLowerCase().includes("lpm") ? displayValue : `${displayValue} LPM`);
      return;
    }

    if (fieldName === "tubeSize") {
      parts.push(displayValue.toLowerCase().includes("mm") ? displayValue : `${displayValue} mm`);
      return;
    }

    if (fieldName === "numberAtLips") {
      parts.push(displayValue.toLowerCase().includes("cm") ? displayValue : `${displayValue} cm`);
      return;
    }

    if (field?.label && (displayValue === "Yes" || displayValue === "No")) {
      parts.push(`${field.label}: ${displayValue}`);
      return;
    }

    parts.push(displayValue);
  });

  return parts;
}

export function buildTreatmentNarrative(treatments = []) {
  return treatments
    .map((treatment) => {
      const details = buildTreatmentSummaryParts(treatment).join(", ");

      const base = [
        treatment.category,
        treatment.type,
        treatment.timestamp,
        treatment.performedBy,
      ]
        .filter(Boolean)
        .join(" • ");

      return [
        base,
        details,
        treatment.successful ? `Successful: ${treatment.successful}` : "",
        treatment.notes,
      ]
        .filter(Boolean)
        .join(" | ");
    })
    .filter(Boolean)
    .join("\n");
}